import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getMaintenanceSettings } from "store/services/dashboardServices";
import { PortalMode, resolvePortalMode } from "utils/portalMode";

const RECHECK_INTERVAL = 60 * 1000;

/**
 * Tracks the platform-wide `customer_portal_mode`. `mode` stays `null` until
 * the first check settles so the caller can hold the first render instead of
 * flashing the portal before the maintenance page.
 *
 * The mode is re-read as the customer moves around (at most once a minute), so
 * a session that was already open picks up a switch to maintenance, and it is
 * polled while the portal is down so the page clears itself once it is back.
 */
export const usePortalMode = () => {
  const location = useLocation();
  const [mode, setMode] = useState<PortalMode | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const requestIdRef = useRef(0);
  const lastCheckedAtRef = useRef(0);

  const checkPortalMode = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    lastCheckedAtRef.current = Date.now();
    setIsChecking(true);

    // Only an explicit answer from the API may take the portal down. A check
    // that fails for any reason (network, timeout, server error) counts as
    // live, even if the portal was in maintenance a moment ago.
    let nextMode: PortalMode = "live";

    try {
      nextMode = resolvePortalMode(await getMaintenanceSettings());
    } catch {}

    if (requestIdRef.current !== requestId) {
      return;
    }

    setMode(nextMode);
    setIsChecking(false);
  }, []);

  useEffect(() => {
    if (Date.now() - lastCheckedAtRef.current >= RECHECK_INTERVAL) {
      checkPortalMode();
    }
  }, [checkPortalMode, location.pathname]);

  useEffect(() => {
    if (!mode || mode === "live") {
      return;
    }

    const intervalId = setInterval(checkPortalMode, RECHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [checkPortalMode, mode]);

  return { mode, isChecking, checkPortalMode };
};
